import type { RequestAttemptEvent } from './api';

/** Lifecycle state for one non-idempotent request, including a possible replay. */
export interface MutationAttemptTracker {
  /** Observer passed to the request helper. */
  observe: (event: RequestAttemptEvent) => void;
  /** True when the final observed response proves the mutation was accepted. */
  wasAccepted: () => boolean;
  /** True when dispatch occurred but the final outcome does not prove refusal. */
  couldHaveCommitted: () => boolean;
}

/**
 * Track the final network attempt of one non-idempotent mutation. A reactive
 * authentication replay emits a new dispatched event, which clears the prior
 * response so only the attempt that can still determine the outcome is used.
 */
export function createMutationAttemptTracker(): MutationAttemptTracker {
  let dispatched = false;
  let responseStatus: number | null = null;

  return {
    observe(event) {
      if (event.type === 'dispatched') {
        dispatched = true;
        responseStatus = null;
        return;
      }
      responseStatus = event.status;
    },
    wasAccepted() {
      return (
        dispatched &&
        responseStatus !== null &&
        responseStatus >= 200 &&
        responseStatus < 300
      );
    },
    couldHaveCommitted() {
      if (!dispatched) return false;
      if (responseStatus === null) return true;
      return (
        (responseStatus >= 200 && responseStatus < 300) || responseStatus >= 500
      );
    },
  };
}
